import {
  addDays,
  addMonths,
  addWeeks,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  isSameYear,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import type { ScheduledShift } from "../types";

export type Granularity = "day" | "week" | "month";

export interface PeriodRange {
  /** Inclusive lower bound (local midnight). */
  startDate: Date;
  /** Exclusive upper bound (local midnight of the day after the range). */
  endDate: Date;
  granularity: Granularity;
}

export interface PeriodChip extends PeriodRange {
  /** Short human label, e.g. "23/3-29/3", "23 Mar", "Mar 2026". */
  label: string;
  /** Stable key safe to use as React key / map index. */
  key: string;
}

/** Monday-based week start to match the screenshot layout. */
const WEEK_STARTS_ON = 1 as const;

/**
 * Compute the [start, end) window (end-exclusive) for the period that
 * contains `anchor` at the given granularity. Bounds are local midnight.
 */
export function getPeriodRange(anchor: Date, granularity: Granularity): PeriodRange {
  switch (granularity) {
    case "day": {
      const startDate = startOfDay(anchor);
      return {
        startDate,
        endDate: addDays(startDate, 1),
        granularity,
      };
    }
    case "week": {
      const startDate = startOfWeek(anchor, { weekStartsOn: WEEK_STARTS_ON });
      const lastDay = endOfWeek(anchor, { weekStartsOn: WEEK_STARTS_ON });
      return {
        startDate,
        endDate: addDays(startOfDay(lastDay), 1),
        granularity,
      };
    }
    case "month": {
      const startDate = startOfMonth(anchor);
      const lastDay = endOfMonth(anchor);
      return {
        startDate,
        endDate: addDays(startOfDay(lastDay), 1),
        granularity,
      };
    }
  }
}

/**
 * Shift the given period by `delta` units (negative = earlier). The anchor
 * used for recomputation is the period's start.
 */
export function shiftPeriod(range: PeriodRange, delta: number): PeriodRange {
  switch (range.granularity) {
    case "day":
      return getPeriodRange(addDays(range.startDate, delta), "day");
    case "week":
      return getPeriodRange(addWeeks(range.startDate, delta), "week");
    case "month":
      return getPeriodRange(addMonths(range.startDate, delta), "month");
  }
}

/**
 * Short label for a chip (compact, no year unless it differs from current).
 * - day:   "23 Mar" or "23 Mar 2025"
 * - week:  "23/3-29/3" or "23/12/25-2/1/26" when crossing year
 * - month: "Mar" or "Mar 2025"
 */
export function formatPeriodChipLabel(range: PeriodRange, now: Date = new Date()): string {
  const { startDate, endDate, granularity } = range;
  const lastDay = addDays(endDate, -1);
  switch (granularity) {
    case "day":
      return isSameYear(startDate, now)
        ? format(startDate, "d MMM")
        : format(startDate, "d MMM yyyy");
    case "week": {
      const crossesYear = !isSameYear(startDate, lastDay);
      const startsOtherYear = !isSameYear(startDate, now);
      if (crossesYear || startsOtherYear) {
        return `${format(startDate, "d/M/yy")}-${format(lastDay, "d/M/yy")}`;
      }
      return `${format(startDate, "d/M")}-${format(lastDay, "d/M")}`;
    }
    case "month":
      return isSameYear(startDate, now)
        ? format(startDate, "MMM")
        : format(startDate, "MMM yyyy");
  }
}

/**
 * Longer label for the summary card (e.g. green banner).
 * - day:   "Monday, 23 March"
 * - week:  "Week of 23/3-29/3"
 * - month: "March 2026"
 */
export function formatPeriodSummaryLabel(
  range: PeriodRange,
  now: Date = new Date()
): string {
  const { startDate, endDate, granularity } = range;
  const lastDay = addDays(endDate, -1);
  switch (granularity) {
    case "day":
      return isSameYear(startDate, now)
        ? format(startDate, "EEEE, d MMMM")
        : format(startDate, "EEEE, d MMMM yyyy");
    case "week": {
      const crossesYear = !isSameYear(startDate, lastDay);
      if (crossesYear) {
        return `Week of ${format(startDate, "d/M/yy")}-${format(lastDay, "d/M/yy")}`;
      }
      if (isSameMonth(startDate, lastDay)) {
        return `Week of ${format(startDate, "d")}-${format(lastDay, "d MMM")}`;
      }
      return `Week of ${format(startDate, "d/M")}-${format(lastDay, "d/M")}`;
    }
    case "month":
      return format(startDate, "MMMM yyyy");
  }
}

/**
 * Build an ordered list of chips surrounding the selected range so the user
 * can move to neighbouring periods without clicking next/prev. The selected
 * range itself is always included.
 */
export function buildPeriodChips(
  selected: PeriodRange,
  { before = 2, after = 2 }: { before?: number; after?: number } = {}
): PeriodChip[] {
  const chips: PeriodChip[] = [];
  for (let i = -before; i <= after; i += 1) {
    const range = shiftPeriod(selected, i);
    chips.push({
      ...range,
      label: formatPeriodChipLabel(range),
      key: buildPeriodKey(range),
    });
  }
  return chips;
}

/** Stable key string for a range; useful for React keys and cache lookups. */
export function buildPeriodKey(range: PeriodRange): string {
  return `${range.granularity}:${range.startDate.toISOString()}`;
}

/** ISO datetime string for the start (inclusive) bound. */
export function toApiStart(range: PeriodRange): string {
  return range.startDate.toISOString();
}

/** ISO datetime string for the end (exclusive) bound. */
export function toApiEnd(range: PeriodRange): string {
  return range.endDate.toISOString();
}

/** Duration of a planned shift in milliseconds; negative/invalid values => 0. */
export function scheduledDurationMs(shift: ScheduledShift): number {
  const start = Date.parse(shift.planned_start_at);
  const end = Date.parse(shift.planned_end_at);
  if (Number.isNaN(start) || Number.isNaN(end)) return 0;
  return Math.max(0, end - start);
}

/**
 * Format a millisecond duration as "Xh Ym", with fallbacks for very short
 * durations. 0 → "0h 0m".
 */
export function formatDurationHm(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "0h 0m";
  const totalMinutes = Math.round(ms / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}h ${minutes.toString().padStart(2, "0")}m`;
}

/** Sum of planned durations across a list of shifts, in milliseconds. */
export function totalScheduledDurationMs(shifts: ScheduledShift[]): number {
  return shifts.reduce((acc, shift) => acc + scheduledDurationMs(shift), 0);
}

interface TimelineDayItem {
  planned_start_at: string;
  timezone?: string | null;
}

export interface ScheduledShiftDayGroup<T extends TimelineDayItem = ScheduledShift> {
  /** Local-day key in the shift's timezone (yyyy-MM-dd). */
  dayKey: string;
  /** UPPERCASE human label, e.g. "MONDAY, 23 MARCH". */
  label: string;
  shifts: T[];
}

/**
 * Group shifts by their planned-start calendar day in the shift's timezone
 * (falls back to local time if the timezone is missing). Groups are returned
 * chronologically by day, shifts within a day sorted by start time.
 *
 * The function is generic so callers using subtypes (e.g.
 * `ScheduledShiftDisplayItem`) preserve type information.
 */
export function groupScheduledShiftsByDay<T extends TimelineDayItem>(
  shifts: T[]
): ScheduledShiftDayGroup<T>[] {
  const byDay = new Map<string, { sample: Date; shifts: T[] }>();

  for (const shift of shifts) {
    const parsed = new Date(shift.planned_start_at);
    if (Number.isNaN(parsed.getTime())) continue;
    const dayKey = formatDayKeyInZone(parsed, shift.timezone);
    const existing = byDay.get(dayKey);
    if (existing) {
      existing.shifts.push(shift);
    } else {
      byDay.set(dayKey, { sample: parsed, shifts: [shift] });
    }
  }

  const groups: ScheduledShiftDayGroup<T>[] = Array.from(byDay.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([dayKey, { sample, shifts: groupShifts }]) => {
      const label = formatDayHeaderInZone(sample, groupShifts[0]?.timezone);
      groupShifts.sort(
        (a, b) =>
          Date.parse(a.planned_start_at) - Date.parse(b.planned_start_at)
      );
      return { dayKey, label, shifts: groupShifts };
    });
  return groups;
}

/** yyyy-MM-dd in the given timezone. */
function formatDayKeyInZone(date: Date, timezone?: string | null): string {
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      timeZone: timezone || undefined,
    }).format(date);
    return parts; // en-CA already renders as yyyy-MM-dd
  } catch {
    return format(date, "yyyy-MM-dd");
  }
}

/**
 * Returns true when `iso` falls on the same calendar day as `now` in the
 * given timezone. Used to decide whether a scheduled shift is "today" for
 * the Start Shift button.
 */
export function isSameDayInZone(
  iso: string | null | undefined,
  timezone?: string | null,
  now: Date = new Date()
): boolean {
  if (!iso) return false;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return false;
  return (
    formatDayKeyInZone(parsed, timezone) ===
    formatDayKeyInZone(now, timezone)
  );
}

/** "MONDAY, 23 MARCH" in the given timezone. */
function formatDayHeaderInZone(date: Date, timezone?: string | null): string {
  try {
    const formatter = new Intl.DateTimeFormat("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      timeZone: timezone || undefined,
    });
    const parts = formatter.formatToParts(date);
    const weekday = parts.find((p) => p.type === "weekday")?.value ?? "";
    const day = parts.find((p) => p.type === "day")?.value ?? "";
    const month = parts.find((p) => p.type === "month")?.value ?? "";
    // Some Intl implementations (e.g. Node's ICU data) omit the comma; build
    // the label explicitly so the rendered header is stable across platforms.
    return `${weekday}, ${day} ${month}`.toUpperCase();
  } catch {
    return format(date, "EEEE, d MMMM").toUpperCase();
  }
}
